import { useState, useEffect } from 'react';
import { X, Edit2, Loader2, FileText, History, Trash2, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { purchasingAPI, ContractPrice, ContractPriceChangeLog } from '../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { usePreferences } from '../../contexts/PreferencesContext';

interface ContractPriceDetailModalProps {
  contractId: number;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (contract: ContractPrice) => void;
}

export default function ContractPriceDetailModal({
  contractId,
  isOpen,
  onClose,
  onEdit
}: ContractPriceDetailModalProps) {
  const { formatDate } = usePreferences();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<ContractPrice | null>(null);
  const [changeLogs, setChangeLogs] = useState<ContractPriceChangeLog[]>([]);
  const [activeTab, setActiveTab] = useState('details');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen && contractId) {
      fetchContractDetails();
    }
  }, [isOpen, contractId]);

  const fetchContractDetails = async () => {
    try {
      setLoading(true);
      const [contractRes, changeLogRes] = await Promise.all([
        purchasingAPI.getContractPrice(contractId),
        purchasingAPI.getContractPriceChangelog(contractId)
      ]);
      setContract(contractRes.data);
      setChangeLogs(changeLogRes.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to load contract details:', err);
      toast.error('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contract) return;

    const ok = await confirm({ title: 'Delete Contract', description: `Are you sure you want to delete contract "${contract.contract_number}"? This action cannot be undone.`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    setDeleting(true);
    try {
      await purchasingAPI.deleteContractPrice(contract.id);
      toast.success('Contract deleted successfully');
      onClose();
    } catch (err: unknown) {
      console.error('Failed to delete contract:', err);
      toast.error('Failed to delete contract');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              Contract Details
            </h2>
            {contract && getStatusBadge(contract.status)}
          </div>
          <div className="flex items-center gap-2">
            {contract && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(contract)}
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </>
            )}
            <button onClick={onClose} className="p-1 hover:bg-muted rounded ml-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content — CR-4878: this wrapper and its Tabs/scroll descendants
            must all be flex-col with min-h-0 so overflow-y-auto actually
            scrolls when content exceeds the modal's max-h-[90vh]. Using
            `h-full` on the Tabs didn't work because the parent's computed
            height comes from the flex algorithm, not an explicit size, so
            Tabs grew to its content (~769px) and spilled past the modal
            footer — clipping the Notes section. Making the wrapper itself
            a flex-col and giving Tabs flex-1 forces the flex algorithm to
            constrain Tabs to the remaining space. */}
        <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : contract ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <div className="border-b px-4">
                <TabsList>
                  <TabsTrigger value="details" className="gap-2">
                    <FileText className="h-4 w-4" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="history" className="gap-2">
                    <History className="h-4 w-4" />
                    History ({changeLogs.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <TabsContent value="details" className="m-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Number</label>
                      <p className="font-mono font-semibold text-lg">{contract.contract_number}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Customer</label>
                      <p className="font-medium">{contract.customer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Supplier</label>
                      <p className="font-medium">{contract.supplier_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Valid From</label>
                      <p className="font-medium">{formatDate(contract.valid_from)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Valid To</label>
                      <p className="font-medium">{formatDate(contract.valid_to)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Currency</label>
                      <p className="font-medium">{contract.currency}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Owner</label>
                      <p className="font-medium">{contract.owner_name || contract.owner_details?.full_name || 'Not assigned'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Auto-Renew</label>
                      <p className={`font-medium ${contract.auto_renew ? 'text-green-600' : ''}`}>
                        {contract.auto_renew ? 'Yes' : 'No'}
                        {contract.auto_renew && contract.renewal_period_years ? ` (${contract.renewal_period_years}yr)` : ''}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Risk Category</label>
                      <p className="font-medium capitalize">{contract.risk_category || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Notice Period</label>
                      <p className="font-medium">
                        {contract.notice_period_months != null
                          ? `${contract.notice_period_months} ${contract.notice_period_months === 1 ? 'month' : 'months'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Reminder (days before notice)</label>
                      <p className="font-medium">
                        {contract.reminder_days != null
                          ? `${contract.reminder_days} ${contract.reminder_days === 1 ? 'day' : 'days'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">BR Frequency</label>
                      <p className="font-medium">
                        {contract.br_frequency_months != null
                          ? `${contract.br_frequency_months} ${contract.br_frequency_months === 1 ? 'month' : 'months'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Negotiation</label>
                      <p className="font-medium">{contract.contract_negotiation ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract File</label>
                      {contract.contract_file ? (
                        // CR-4880: Route through the authenticated API
                        // download proxy instead of linking to the raw
                        // contract_file URL. The direct S3 link was failing
                        // to open on staging (no click action). The
                        // proxy endpoint handles auth + streams the file
                        // regardless of the underlying storage shape.
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await purchasingAPI.downloadContractFile(contract.id);
                              const contentType = response.headers?.['content-type'];
                              const blob = new Blob([response.data], {
                                type: typeof contentType === 'string' ? contentType : 'application/octet-stream',
                              });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = contract.contract_file!.split('/').pop() || 'contract';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch {
                              toast.error('Failed to download contract file');
                            }
                          }}
                          className="font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          {contract.contract_file.split('/').pop()}
                        </button>
                      ) : (
                        <p className="font-medium text-muted-foreground">No file uploaded</p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Created By</label>
                      <p className="font-medium">{contract.created_by_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Created At</label>
                      <p className="font-medium">{formatDate(contract.created_at)}</p>
                    </div>
                    {/* CR-4879: Total Parts field removed from the high-level
                        contract detail view — parts count lives on the
                        dedicated parts breakdown, not this summary. */}
                    {contract.notes && (
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="text-xs text-muted-foreground uppercase tracking-wider block">Notes</label>
                        {/* CR-4878: whitespace-pre-wrap + break-words keep
                            long or multi-line notes readable inside the
                            pop-up instead of overflowing horizontally. */}
                        <p className="text-sm mt-1 p-3 bg-muted/30 rounded-lg whitespace-pre-wrap break-words">{contract.notes}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="history" className="m-0">
                  {changeLogs.length > 0 ? (
                    <div className="space-y-3">
                      {changeLogs.map((log) => (
                        <div key={log.id} className="p-4 border rounded-lg bg-muted/10">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">
                                {log.field_changed}
                                {log.contract_part_number && (
                                  <span className="text-muted-foreground ml-2">
                                    (Part: {log.contract_part_number})
                                  </span>
                                )}
                              </p>
                              <div className="flex items-center gap-2 mt-1 text-sm">
                                <span className="text-red-600 line-through">{log.old_value || '(empty)'}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-green-600">{log.new_value || '(empty)'}</span>
                              </div>
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                              <p>{log.changed_by_name || 'Unknown'}</p>
                              <p>{formatDate(log.changed_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-muted/30 rounded-lg">
                      <History className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No change history available</p>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Contract not found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
